import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useSelector } from 'react-redux';

import { IconCloseSvg } from '../../../assets/inventory';
import {
	ChipBluePng,
	ChipRedPng,
	ChipStackPng,
	IconClockSvg,
	IconInfoSvg,
	TierBlueBgPng,
	TierGoldBgPng,
	TierPurpleBgPng,
	TimerDividerSvg,
	TrophyBronzePng,
	TrophyGoldPng,
	TrophySilverPng,
} from '../../../assets/tournaments/redesign';
import i18n from '../../../i18n/i18n';
import { RootState } from '../../../store';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import {} from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import type { SnG } from '../Middle/TournamentsSection';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';
import { formatLevelErrorMessage } from './registrationErrorUtils';
import {
	BANNER_FILL,
	GOLD_BUTTON_FILL,
	groupThousands,
	orangeGlow,
	PANEL_FILL,
	PANEL_RIM,
	prizePlaces,
	RED_BUTTON_FILL,
	roomIdOf,
	ROW_FILL,
	TOURNAMENT_FONT as FONT,
	tournamentColors as colors,
	tournamentTarget,
	tournamentTitle,
} from './tournamentModalTheme';
import TournamentRulesRedesignModal from './TournamentRulesRedesignModal';
import { splitCountdown, useCountdown } from './useCountdown';
import { useTournamentRegistration } from './useTournamentRegistration';

/**
 * Figma "Modal / Tournament Info — with Timer" (5157:12764) on page "PAVEL DRAFT", section
 * "Tournaments Elements", as drawn over the tournaments screen in 5157:13233: the tier's prizes,
 * the entry banner, the start countdown, the register / unregister action and the seated players.
 * The "i" beside the prize total opens "Modal / Tournament Rules" (5157:14681).
 *
 * The panel chrome is `RedesignModal`'s; everything here is in the 358-wide design space.
 */
const PANEL = { height: 776, radius: 10, paddingX: 12, paddingY: 20, gap: 16 };
const TOTAL_SEATS = 8;

type Tier = { trophy: any; plate: any; border: string; fill: Array<[string, string]> };
/** "Tiers": first, second, third - each with its trophy, textured plate and rim. */
const TIERS: Tier[] = [
	{
		trophy: TrophyGoldPng,
		plate: TierGoldBgPng,
		border: '#E89200',
		fill: [
			['0', '#5A3A08'],
			['1', '#2A1A02'],
		],
	},
	{
		trophy: TrophySilverPng,
		plate: TierBlueBgPng,
		border: '#3B7FD4',
		fill: [
			['0', '#0B2A5A'],
			['1', '#061428'],
		],
	},
	{
		trophy: TrophyBronzePng,
		plate: TierPurpleBgPng,
		border: '#FF3B2F',
		fill: [
			['0', '#5A0A0A'],
			['1', '#2A0505'],
		],
	},
];

type Props = {
	sng: SnG;
	onClose: () => void;
};

export const TournamentInfoModal = ({ sng: sngProp, onClose }: Props) => {
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);
	const s = (n: number) => n * scale;
	const [rulesOpen, setRulesOpen] = useState(false);
	const playerId = useSelector((state: RootState) => state.auth?.user?.id);

	const tournamentId = String(sngProp?.id ?? '');
	const {
		sng,
		details,
		players,
		signedPlayers,
		isSigned,
		registerLoading,
		registerActionPending,
		registrationFeedback,
		handleRegisterPress,
	} = useTournamentRegistration({ tournamentId, roomId: roomIdOf(sngProp), sng: sngProp });

	const tournament = sng ?? sngProp;
	const places = prizePlaces(tournament);
	const prizeTotal = places.reduce((sum, value) => sum + value, 0);
	const entryFee = Number(tournament?.entry_size ?? 0);
	const tournamentChips = Number(details?.tournament_chips ?? tournament?.tournament_chips ?? 0);
	const seats = Number(tournament?.players) || TOTAL_SEATS;

	const target = tournamentTarget(details ?? tournament) ?? tournamentTarget(tournament);
	const left = useCountdown(target);
	const countdown = splitCountdown(left);
	const schedule = useMemo(() => {
		if (target == null) return null;
		const date = new Date(target);
		const day = date.toLocaleDateString(i18n.language, { weekday: 'long' });
		const time = date.toLocaleTimeString(i18n.language, {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});
		return t('T_SITE_TOURNAMENT_STARTS_AT', {
			defaultValue: 'Starts {{day}} at {{time}}',
			day,
			time,
		}).toUpperCase();
	}, [target, t]);

	const { showLevelError, showBalanceError, shownRequiredLevel } = registrationFeedback;
	const feedback = showLevelError
		? formatLevelErrorMessage(t('T_SITE_TOURNAMENT_PLAY_LEVEL_ERROR'), shownRequiredLevel)
		: showBalanceError
			? t('T_ServerError_14')
			: null;

	const levelOf = (id: string | number) => {
		const raw = players.find((p: any) => String(p.id ?? p.user_id ?? p.player_id) === String(id));
		const level = Number(raw?.level ?? raw?.player_level);
		return Number.isFinite(level) && level > 0 ? level : null;
	};

	const busy = registerLoading || registerActionPending;

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				fill: PANEL_FILL,
				rim: PANEL_RIM,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				{/* "Header" (5157:12765) */}
				<View style={styles.titleBar}>
					<Text numberOfLines={1} style={styles.title}>
						{tournamentTitle(tournament, t)}
					</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_TOURNAMENT_INFO', {
							defaultValue: 'Close tournament info',
						})}
						onPress={onClose}
						hitSlop={8}
						style={styles.close}
					>
						<IconCloseSvg width={s(24)} height={s(24)} />
					</Pressable>
				</View>

				{/* "Section Header — PRIZES" (5157:12773): the total and the rules button. */}
				<View style={styles.sectionHeader}>
					<Text style={styles.overline}>
						{t('T_SITE_TOURNAMENT_PRIZES', { defaultValue: 'Prizes' }).toUpperCase()}
					</Text>
					<Text style={styles.numericS}>{groupThousands(prizeTotal)}</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_TOURNAMENT_RULES', {
							defaultValue: 'Tournament rules',
						})}
						onPress={() => setRulesOpen(true)}
						hitSlop={8}
					>
						<IconInfoSvg width={s(24)} height={s(24)} />
					</Pressable>
				</View>

				<RedesignScrollView
					contentContainerStyle={styles.scrollContent}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					{/* "Tiers" (5157:12778) */}
					<View style={styles.tiers}>
						{TIERS.map((tier, index) => (
							<View key={index} style={[styles.tier, { borderColor: tier.border }]}>
								<View style={styles.tierPlate}>
									<GradientFill stops={tier.fill} radius={s(8)} />
									<Image source={tier.plate} style={styles.tierPlateImage} resizeMode='cover' />
								</View>
								<Image source={tier.trophy} style={styles.trophy} resizeMode='contain' />
								<Image source={ChipStackPng} style={styles.chipStack} resizeMode='contain' />
								<Text numberOfLines={1} style={styles.numericM}>
									{groupThousands(places[index])}
								</Text>
							</View>
						))}
					</View>

					{/* "Tournament Entry Banner / Mobile" (5157:13204) */}
					<View style={styles.banner}>
						<GradientFill stops={BANNER_FILL} radius={s(8)} />
						<View style={styles.equation}>
							<View style={styles.equationSide}>
								<Text style={styles.bannerLabel}>
									{t('T_SITE_TOURNAMENT_PLAYERS_ENTRY').toUpperCase()}
								</Text>
								<View style={styles.bannerValueRow}>
									<Image source={ChipRedPng} style={styles.entryChip} resizeMode='contain' />
									<Text style={styles.bannerValue}>{groupThousands(entryFee)}</Text>
								</View>
							</View>
							<Text style={styles.equals}>=</Text>
							<View style={styles.equationSide}>
								<Text style={styles.bannerLabel}>
									{t('T_SITE_TOURNAMENT_PLAYERS_CHIPS').toUpperCase()}
								</Text>
								<View style={styles.bannerValueRow}>
									<Image source={ChipBluePng} style={styles.tournamentChip} resizeMode='contain' />
									<Text style={styles.bannerValue}>{groupThousands(tournamentChips)}</Text>
								</View>
							</View>
						</View>
						<GlowDivider width={s(260)} height={Math.max(1, s(1))} />
						<Text style={styles.bannerCaption}>
							{t('T_SITE_TOURNAMENT_TIMER_CLOSE', {
								defaultValue:
									'Cancellation of the tournament if 8 players are not available in the following:',
							})}
						</Text>
					</View>

					{/* "Timer / Segmented Bar" (5157:13173) */}
					<View style={styles.timer}>
						{schedule && (
							<View style={styles.schedule}>
								<IconClockSvg width={s(16)} height={s(16)} />
								<Text style={styles.scheduleText}>{schedule}</Text>
							</View>
						)}
						<View style={styles.capsule}>
							<GradientFill stops={ROW_FILL} radius={s(26)} />
							{(
								[
									[countdown.days, t('T_SITE_TOURNAMENT_DAYS', { defaultValue: 'Days' })],
									[countdown.hours, t('T_SITE_TOURNAMENT_HOURS', { defaultValue: 'Hrs' })],
									[countdown.minutes, t('T_SITE_TOURNAMENT_MINUTES', { defaultValue: 'Min' })],
									[countdown.seconds, t('T_SITE_TOURNAMENT_SECONDS', { defaultValue: 'Sec' })],
								] as const
							).map(([value, label], index) => (
								<React.Fragment key={index}>
									{index > 0 && <TimerDividerSvg width={s(3)} height={s(46)} />}
									<View style={styles.segment}>
										<Text style={styles.segmentValue}>{value}</Text>
										<Text numberOfLines={1} style={styles.segmentLabel}>
											{label.toUpperCase()}
										</Text>
									</View>
								</React.Fragment>
							))}
						</View>
					</View>

					{/* "Actions" (5157:12880): red to leave, the gold modal action to join. */}
					<Pressable
						accessibilityRole='button'
						disabled={busy}
						onPress={handleRegisterPress}
						style={({ pressed }) => [
							styles.action,
							!isSigned && styles.actionRegister,
							(pressed || busy) && styles.pressed,
						]}
					>
						<GradientFill stops={isSigned ? RED_BUTTON_FILL : GOLD_BUTTON_FILL} radius={s(10)} />
						<Text style={[styles.actionLabel, !isSigned && styles.actionLabelRegister]}>
							{(isSigned
								? t('T_SITE_TOURNAMENT_UNREGISTER')
								: t('T_SITE_TOURNAMENT_REGISTER')
							).toUpperCase()}
						</Text>
					</Pressable>
					{feedback ? <Text style={styles.feedback}>{feedback}</Text> : null}

					{/* "Section / Registered Players" (5157:12954) */}
					<View style={styles.players}>
						<View style={styles.sectionHeader}>
							<Text style={styles.overline}>
								{t('T_SITE_TOURNAMENT_REGISTERED_PLAYERS', {
									defaultValue: 'Registered players',
								}).toUpperCase()}
							</Text>
							<Text style={styles.numericS}>
								{signedPlayers.length}/{seats}
							</Text>
						</View>
						{signedPlayers.map(player => {
							const isYou = playerId != null && String(player.id) === String(playerId);
							const level = levelOf(player.id);
							return (
								<View key={String(player.id)} style={styles.playerRow}>
									<GradientFill stops={ROW_FILL} radius={s(8)} />
									<View style={styles.avatarWrap}>
										<View style={styles.avatar}>
											<Image
												source={player.avatarImage}
												style={styles.avatarImage}
												resizeMode='cover'
											/>
										</View>
										{level != null && (
											<View style={styles.levelBadge}>
												<View style={styles.levelDisc}>
													<Text style={styles.levelText}>{level}</Text>
												</View>
											</View>
										)}
									</View>
									<Text
										numberOfLines={1}
										style={[styles.playerName, isYou && styles.playerNameYou]}
									>
										{player.name}
									</Text>
									<Image source={ChipRedPng} style={styles.playerChip} resizeMode='contain' />
									<Text numberOfLines={1} style={styles.numericM}>
										{groupThousands(Number(player.chips) || 0)}
									</Text>
								</View>
							);
						})}
					</View>
				</RedesignScrollView>
			</View>

			{rulesOpen && <TournamentRulesRedesignModal onClose={() => setRulesOpen(false)} />}
		</RedesignModal>
	);
};

/** "Divider Glow": a 1pt bright-gold rule that fades to nothing at both ends. */
const GlowDivider = ({ width, height }: { width: number; height: number }) => {
	const id = useSvgId('tournamentBannerDivider');
	return (
		<Svg width={width} height={height}>
			<Defs>
				<LinearGradient id={id} x1='0' y1='0' x2='1' y2='0'>
					<Stop offset='0' stopColor='#FFFFFF' stopOpacity={0} />
					<Stop offset='0.5' stopColor={colors.brightGold} stopOpacity={1} />
					<Stop offset='1' stopColor='#FFFFFF' stopOpacity={0} />
				</LinearGradient>
			</Defs>
			<Rect width={width} height={height} fill={`url(#${id})`} />
		</Svg>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const hairline = Math.max(1, s(1));
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		pressed: { opacity: 0.8 },
		titleBar: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(18),
			lineHeight: s(18 * 1.3),
			color: colors.accent,
		},
		close: {
			width: s(32),
			height: s(32),
			alignItems: 'center',
			justifyContent: 'center',
		},
		sectionHeader: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		overline: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(10),
			lineHeight: s(12),
			letterSpacing: s(0.6),
			color: colors.gold300,
		},
		numericS: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(14),
			letterSpacing: s(-0.14),
			color: colors.accent,
		},
		numericM: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(16),
			lineHeight: s(16),
			letterSpacing: s(-0.16),
			color: colors.accent,
		},
		// The design keeps 12 on the right for the 4-wide scroll track and its gap.
		scrollContent: {
			paddingRight: s(12),
		},
		tiers: {
			flexDirection: 'row',
			gap: s(8),
		},
		tier: {
			flex: 1,
			height: s(149),
			alignItems: 'center',
			gap: s(6),
			padding: s(8),
			borderRadius: s(8),
			borderWidth: hairline,
			...orangeGlow(s(5)),
		},
		tierPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(8),
			overflow: 'hidden',
		},
		tierPlateImage: {
			width: '100%',
			height: '100%',
		},
		trophy: {
			width: s(75),
			height: s(68),
		},
		chipStack: {
			width: s(50),
			height: s(28),
		},
		banner: {
			marginTop: s(10),
			alignItems: 'center',
			gap: s(10),
			paddingHorizontal: s(12),
			paddingVertical: s(14),
			borderRadius: s(8),
			borderWidth: hairline,
			borderColor: colors.brightGold,
			...orangeGlow(s(5)),
		},
		equation: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(20),
		},
		equationSide: {
			alignItems: 'center',
			gap: s(4),
		},
		bannerLabel: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(12),
			letterSpacing: s(0.4),
			color: colors.gold50,
			textShadowColor: 'rgba(0,0,0,0.35)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(2),
		},
		bannerValueRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(6),
		},
		entryChip: {
			width: s(21),
			height: s(21),
		},
		tournamentChip: {
			width: s(28),
			height: s(28),
		},
		bannerValue: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(16),
			color: colors.gold200,
		},
		equals: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(24),
			color: colors.gold50,
		},
		bannerCaption: {
			width: s(260),
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(16),
			color: colors.gold50,
			textAlign: 'center',
		},
		timer: {
			marginTop: s(8),
			alignItems: 'center',
			gap: s(8),
		},
		schedule: {
			height: s(16),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		scheduleText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(10),
			lineHeight: s(12),
			letterSpacing: s(0.35),
			color: colors.gold100,
		},
		capsule: {
			width: '100%',
			height: s(52),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(8),
			borderRadius: s(26),
			borderWidth: Math.max(1, s(2)),
			borderColor: colors.brightGold,
			overflow: 'hidden',
		},
		segment: {
			flex: 1,
			height: s(46),
			alignItems: 'center',
			justifyContent: 'center',
		},
		segmentValue: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(28),
			lineHeight: s(30),
			color: colors.gold200,
		},
		segmentLabel: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(8),
			lineHeight: s(10),
			letterSpacing: s(1),
			color: colors.secondary,
		},
		action: {
			marginTop: s(14),
			height: s(47),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(10),
			borderWidth: hairline,
			borderColor: colors.buttonRim,
			...orangeGlow(s(9), 0.6),
		},
		actionRegister: {
			borderColor: '#FFE45C',
		},
		actionLabel: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(18),
			lineHeight: s(18 * 1.3),
			letterSpacing: s(0.36),
			color: colors.accent,
		},
		actionLabelRegister: {
			color: '#2A0A04',
		},
		feedback: {
			marginTop: s(6),
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(12),
			lineHeight: s(16),
			color: '#FF6A4D',
			textAlign: 'center',
		},
		players: {
			marginTop: s(11),
			gap: s(6),
		},
		playerRow: {
			height: s(54),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(10),
			paddingHorizontal: s(10),
			borderRadius: s(8),
			borderWidth: hairline,
			borderColor: colors.rowBorder,
		},
		avatarWrap: {
			width: s(44),
			height: s(44),
		},
		avatar: {
			width: s(44),
			height: s(44),
			borderRadius: s(22),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: '#F5C747',
			overflow: 'hidden',
			backgroundColor: colors.sunken,
			...orangeGlow(s(4), 0.6),
		},
		avatarImage: {
			width: '100%',
			height: '100%',
		},
		// "Level Badge": a green ring round a gold disc, over the avatar's lower left.
		levelBadge: {
			position: 'absolute',
			left: s(-2),
			top: s(26),
			width: s(22),
			height: s(22),
			borderRadius: s(11),
			borderWidth: Math.max(1, s(2)),
			borderColor: '#41E685',
			backgroundColor: '#12200F',
			alignItems: 'center',
			justifyContent: 'center',
		},
		levelDisc: {
			width: s(16),
			height: s(16),
			borderRadius: s(8),
			backgroundColor: '#F5C747',
			alignItems: 'center',
			justifyContent: 'center',
		},
		levelText: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(9),
			lineHeight: s(11),
			color: '#000000',
		},
		playerName: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(14 * 1.4),
			color: colors.gold50,
		},
		playerNameYou: {
			color: colors.accent,
		},
		playerChip: {
			width: s(32),
			height: s(33),
		},
	});
};

export default TournamentInfoModal;
